/*
 * Copyright (c) CIRROM 2026.
 */

import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Snackbar from "@mui/material/Snackbar";
import Typography from "@mui/material/Typography";
import { QueuedWrite, UNDO_WINDOW_MS } from "./useUndoableWrite";
import classes from "./css/undoSnackbar.module.css";

/*
 * The held decision, and the way back out of it.
 *
 * The countdown is not decoration: the whole promise here is "you have a few
 * seconds", and a steward cannot act on that without being able to see how
 * many are left. It drains left to right so the remaining bar reads as
 * remaining time.
 *
 * Deliberately not an Alert: nothing has succeeded or failed yet. The card's
 * own banner still reports the outcome once the write actually lands, so the
 * two never say the same thing at the same moment.
 */
export const UndoSnackbar = (props: {
  queued: QueuedWrite | null;
  onUndo: () => void;
}) => (
  <Snackbar
    open={props.queued !== null}
    /*
     * No autoHideDuration: the hook's timer already owns the window, and a
     * second countdown could close the snackbar while the decision was still
     * reversible - showing the steward a door that no longer opens.
     */
    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
  >
    <Box className={classes.undoBar} sx={{ backgroundColor: "grey.900" }}>
      <div className={classes.undoBarRow}>
        <Typography variant="body2" sx={{ color: "common.white" }}>
          {props.queued?.label}
        </Typography>
        <Button
          size="small"
          onClick={props.onUndo}
          sx={{
            fontSize: "13px",
            color: "common.white",
            fontWeight: 600,
            "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.08)" },
          }}
        >
          Undo
        </Button>
      </div>
      {/*
       * Keyed on the queued item so a fresh decision restarts the animation
       * rather than inheriting the previous one's remaining time.
       */}
      <Box
        key={props.queued?.key}
        aria-hidden="true"
        className={classes.undoCountdown}
        sx={{ backgroundColor: "common.white" }}
        style={{ animationDuration: `${UNDO_WINDOW_MS}ms` }}
      />
    </Box>
  </Snackbar>
);
